import Select from "react-select";
import { useTranslation } from "react-i18next";
import { getCustomSelectStyles } from "../../../Layouts/admin/shared";
import MultiSelectFilter from "./MultiSelectFilter";

interface ThemeClasses {
  bg: string;
  border: string;
  text: string;
  textSecondary: string;
  cardBg: string;
  cardBorder: string;
  buttonHover: string;
  buttonText: string;
  buttonTextHover: string;
}

interface SingleExtraFilter {
  multi?: false;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  noOptionsMessage?: string;
}

/**
 * Filtro de selección múltiple (hoy, el de patios). `values: []` = todos; el
 * placeholder tiene que decirlo, porque con nada elegido es lo único visible.
 * Las opciones NO llevan una entrada "todos": en un multi, "todos" es no elegir.
 */
interface MultiExtraFilter {
  multi: true;
  values: string[];
  onChange: (v: string[]) => void;
  options: { value: string; label: string }[];
  placeholder: string;
  noOptionsMessage?: string;
}

type ExtraFilter = SingleExtraFilter | MultiExtraFilter;

interface HeaderComponentProps {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  title: string;
  subtitle: string;
  searchQuery: string;
  selectedStatus: string;
  selectedView: "grid" | "list";
  onSearchChange: (v: string) => void;
  onStatusChange: (v: string) => void;
  onViewChange: (v: "grid" | "list") => void;
  onAdd?: () => void;
  addButtonText?: string;
  addButtonShortText?: string;
  showAddButton?: boolean;
  statusOptions?: { value: string; label: string }[];
  searchPlaceholder?: string;
  disableCRUD?: boolean;
  extraFilters?: ExtraFilter[];
  /**
   * Vehículos usaba el combo de "estado" para elegir patio. Ahora el patio es
   * un filtro múltiple en `extraFilters` y esa pantalla no tiene estado que
   * filtrar, así que lo apaga.
   */
  showStatusFilter?: boolean;
}

export default function HeaderComponent({
  themeClasses,
  isDarkMode,
  title,
  subtitle,
  searchQuery,
  selectedStatus,
  selectedView,
  onSearchChange,
  onStatusChange,
  onViewChange,
  onAdd,
  addButtonText,
  addButtonShortText,
  showAddButton = true,
  statusOptions,
  searchPlaceholder,
  disableCRUD = false,
  extraFilters,
  showStatusFilter = true,
}: HeaderComponentProps) {
  const { t } = useTranslation("truckYard");

  // Default status options
  const defaultStatusOptions = [
    { value: "all", label: t("common.allStatuses") },
    { value: "active", label: t("common.active") },
    { value: "inactive", label: t("common.inactive") },
  ];

  const statusesOptions = statusOptions || defaultStatusOptions;
  const addText = addButtonText ?? t("common.new");
  const addShortText = addButtonShortText ?? t("common.new");
  const searchText = searchPlaceholder ?? t("common.search");

  return (
    <div className={`${themeClasses.bg} border-b ${themeClasses.border} p-4 sm:p-6`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4">
        <div className="min-w-0">
          <h1 className={`text-xl sm:text-2xl font-bold ${themeClasses.text}`}>
            {title}
          </h1>
          <p className={`text-sm ${themeClasses.textSecondary} mt-1`}>
            {subtitle}
          </p>
        </div>
        {showAddButton && onAdd && (
          <div className="self-start sm:self-auto flex-shrink-0">
            <button
              onClick={onAdd}
              className="bg-primary-500 hover:bg-primary-400 text-gray-900 px-3 sm:px-4 py-2 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors whitespace-nowrap disabled:opacity-80 disabled:cursor-not-allowed text-sm sm:text-base"
              disabled={disableCRUD}
            >
              <i className="ri-add-line"></i>
              <span className="hidden sm:inline">{addText}</span>
              <span className="sm:hidden">{addShortText}</span>
            </button>
          </div>
        )}
      </div>

      {/* Filters and Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
          {/* Status Filter */}
          {showStatusFilter && (
          <div className="w-full sm:min-w-[200px]">
            <Select
              options={statusesOptions}
              styles={getCustomSelectStyles(isDarkMode)}
              placeholder={statusesOptions[0]?.label || t("common.allStatuses")}
              value={statusesOptions.find((status: any) => status.value === selectedStatus) || statusesOptions[0]}
              onChange={(option: any) => {
                onStatusChange(option?.value === "all" ? "" : option?.value || "");
              }}
              isSearchable
              isClearable
              noOptionsMessage={() => t("common.noStatuses")}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          )}

          {/* Extra Filters */}
          {extraFilters?.map((filter, idx) =>
            filter.multi ? (
              <div key={idx} className="w-full sm:min-w-[220px] sm:max-w-[320px]">
                <MultiSelectFilter
                  isDarkMode={isDarkMode}
                  options={filter.options}
                  values={filter.values}
                  onChange={filter.onChange}
                  placeholder={filter.placeholder}
                  noOptionsMessage={
                    filter.noOptionsMessage || t("common.noOptions")
                  }
                />
              </div>
            ) : (
            <div key={idx} className="w-full sm:min-w-[200px]">
              <Select
                options={filter.options}
                styles={getCustomSelectStyles(isDarkMode)}
                placeholder={filter.placeholder || filter.options[0]?.label || t("common.filter")}
                value={
                  filter.options.find((opt: any) => opt.value === filter.value) ||
                  filter.options[0]
                }
                onChange={(option: any) => {
                  filter.onChange(
                    option?.value === "all" ? "" : option?.value || "",
                  );
                }}
                isSearchable
                isClearable
                noOptionsMessage={() =>
                  filter.noOptionsMessage || t("common.noOptions")
                }
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
            ),
          )}

          {/* Search */}
          <div className="relative w-full sm:w-auto">
            <i
              className={`ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 ${themeClasses.textSecondary} text-sm`}
            ></i>
            <input
              type="text"
              placeholder={searchText}
              className={`pl-10 pr-3 py-2 h-9 ${isDarkMode ? "bg-dark-card" : "bg-gray-50"
                } border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text
                } placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm w-full sm:w-64`}
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex justify-end">
          <div
            className={`flex ${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-lg p-1`}
          >
            <button
              className={`px-3 py-1 rounded-md transition-colors ${selectedView === "grid"
                  ? "bg-primary-500 text-gray-900"
                  : `${themeClasses.buttonText} ${themeClasses.buttonHover}`
                }`}
              onClick={() => onViewChange("grid")}
            >
              <i className="ri-grid-fill"></i>
            </button>
            <button
              className={`px-3 py-1 rounded-md transition-colors ${selectedView === "list"
                  ? "bg-primary-500 text-gray-900"
                  : `${themeClasses.buttonText} ${themeClasses.buttonHover}`
                }`}
              onClick={() => onViewChange("list")}
            >
              <i className="ri-list-check"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
